// Apolo IDE — AttachmentActions (Fase 2).
//
// Botões Copiar / Baixar (+ Abrir no Electron) pra QUALQUER arquivo que a IA
// produz/lê no chat. Funciona em web e Electron, ciente do formato:
//   • código/md/texto → usa o `content` que já está na mensagem (Blob/clipboard),
//     sem ler disco.
//   • imagem/binário   → lê os bytes do disco via IPC (window.apolo.file.readBytes,
//     base64) pra baixar/copiar; "Abrir" usa file.openPath (app padrão do macOS).
//   • web sem conteúdo nem IPC → Baixar/Abrir desabilitam com tooltip honesto.

import { useCallback, useState } from 'react';
import { Check, Copy, Download, ExternalLink } from 'lucide-react';
import { attachmentKind, mimeFor, basename, isTextual } from '../../../lib/attachments.js';

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AttachmentActions({ path, content, root, dataUrl, className = '' }) {
  const [copied, setCopied] = useState(false);
  const kind = attachmentKind(path);
  const name = basename(path);
  const mime = mimeFor(path);
  const hasText = typeof content === 'string' && content.length > 0;
  const isElectron = typeof window !== 'undefined' && !!window.apolo;
  const canReadBytes = isElectron && !!root && !!window.apolo?.file?.readBytes;
  const canOpen = isElectron && !!root && !!window.apolo?.file?.openPath;

  // Bytes do disco (binário/imagem) como base64 — best-effort.
  const fetchBytes = useCallback(async () => {
    if (dataUrl && dataUrl.startsWith('data:')) {
      const comma = dataUrl.indexOf(',');
      return comma >= 0 ? base64ToBytes(dataUrl.slice(comma + 1)) : null;
    }
    if (!canReadBytes) return null;
    try {
      const r = await window.apolo.file.readBytes({ root, filePath: path });
      return r?.base64 ? base64ToBytes(r.base64) : null;
    } catch {
      return null;
    }
  }, [dataUrl, canReadBytes, root, path]);

  const onDownload = useCallback(async () => {
    if (hasText && isTextual(path)) {
      triggerDownload(new Blob([content], { type: mime }), name);
      return;
    }
    const bytes = await fetchBytes();
    if (bytes) {
      triggerDownload(new Blob([bytes], { type: mime }), name);
      return;
    }
    if (hasText) {
      triggerDownload(new Blob([content], { type: mime }), name);
      return;
    }
    // Sem conteúdo nem bytes (ex.: binário só-disco no web) → tenta abrir no app.
    if (canOpen) window.apolo.file.openPath({ root, filePath: path });
  }, [hasText, path, content, mime, name, fetchBytes, canOpen, root]);

  const onCopy = useCallback(async () => {
    try {
      if (hasText && isTextual(path)) {
        await navigator.clipboard.writeText(content);
      } else if (kind === 'image') {
        const bytes = await fetchBytes();
        if (bytes && typeof window !== 'undefined' && window.ClipboardItem && navigator.clipboard?.write) {
          const blob = new Blob([bytes], { type: mime });
          await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
        } else {
          await navigator.clipboard.writeText(path);
        }
      } else {
        await navigator.clipboard.writeText(hasText ? content : path);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard bloqueado — silencioso */
    }
  }, [hasText, path, content, kind, mime, fetchBytes]);

  // Baixar fica disponível quando há texto, bytes no disco, ou data URL.
  const canDownload = hasText || canReadBytes || (dataUrl && dataUrl.startsWith('data:'));
  const downloadTitle = canDownload
    ? 'Baixar'
    : canOpen
      ? 'Abrir no app (download indisponível na web)'
      : 'Download indisponível aqui';

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <button type="button" className="rx-btn" onClick={onCopy} title={copied ? 'Copiado!' : 'Copiar'} aria-label="Copiar arquivo">
        {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        {copied ? 'Copiado' : 'Copiar'}
      </button>
      <button
        type="button"
        className="rx-btn rx-btn--strong"
        onClick={onDownload}
        disabled={!canDownload && !canOpen}
        title={downloadTitle}
        aria-label="Baixar arquivo"
      >
        {canDownload ? <Download size={13} aria-hidden="true" /> : <ExternalLink size={13} aria-hidden="true" />}
        {canDownload ? 'Baixar' : 'Abrir'}
      </button>
    </div>
  );
}
